import React, { useMemo, useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Typography,
  Grid,
  IconButton,
  Stack,
  Paper,
  Divider,
  Chip,
  List,
  ListItem,
  Tooltip,
  ListItemText,
  Drawer,
  CircularProgress,
} from "@mui/material";
import moment from "moment";
import { useDispatch } from "store";
import CustomButton from "components/Button";
import TableComponent from "components/grid";

import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import RestoreOutlinedIcon from "@mui/icons-material/RestoreOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import ChevronLeftOutlinedIcon from "@mui/icons-material/ChevronLeftOutlined";
import ChevronRightOutlinedIcon from "@mui/icons-material/ChevronRightOutlined";

import { useGetFilesByCustomIdQuery, useLazyDownloadFileQuery, useUploadModuleFileMutation } from "store/slices/apis/dmsSlice";
import { useParams } from "react-router-dom";
import UploadDocument from "./UploadDocument";

const labelStyle = {
  fontSize: "12px",
  fontWeight: 600,
  color: "#111827",
};

const subTextStyle = {
  fontSize: "12px",
  color: "#6B7280",
};

function formatBytes(bytes) {
  if (!bytes && bytes !== 0) return "-";
  const sizes = ["B", "KB", "MB", "GB", "TB"];
  if (bytes === 0) return "0 B";
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  const val = bytes / Math.pow(1024, i);
  return `${val.toFixed(val >= 10 || i === 0 ? 0 : 1)} ${sizes[i]}`;
}

function fileTypeFromName(name = "") {
  const ext = name.split(".").pop()?.toLowerCase();
  return ext ? ext.toUpperCase() : "FILE";
}

const mapApiFileToUi = (f, entityType) => ({
  id: f.fileId,
  name: f.fileName,
  version: f.currentVersion,
  sizeBytes: f.fileSize,
  uploadedBy: f.createdBy,
  uploadedOn: f.createdDate,
  linkedTo: entityType ? [{ type: entityType, id: f.customId }] : [],
  _versions: (f.versions || []).map((v) => ({
    version: v.versionNumber,
    date: v.createdDate,
    by: v.createdBy,
    note: "",
    fileVersionId: v.fileVersionId,
    fileId: v.fileId,
    filePath: v.filePath,
    fileSize: v.fileSize,
    contentType: v.contentType,
  })),
});

const DocumentManagement = (props) => {
  const dispatch = useDispatch();
  const { projectId } = useParams();

  const [selectedDocId, setSelectedDocId] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [isUpload, setIsUpload] = useState(false);
  const [loadingDownloadKey, setLoadingDownloadKey] = useState(null);

  // Drawer should open INSIDE dialog, so we render it into this container.
  const dialogBodyRef = useRef(null);

  const customId = props.id; // riskId / complianceId etc.
  const entityType = props.entityType || "Risk";
  const objectName = props.customId || "";
  const isView = props?.isView || false

  const {
    data: apiFiles = [],
    isLoading,
    isFetching,
    isError,
    refetch,
    error
  } = useGetFilesByCustomIdQuery(customId, { skip: !customId });
  console.log("error", error);
  const [uploadFile, { isLoading: uploadingLoader }] = useUploadModuleFileMutation();

  const [triggerDownload, { isFetching: dowloadingLoader }] = useLazyDownloadFileQuery();

  // Supports both:
  // 1) apiFiles is already array (if you used transformResponse)
  // 2) apiFiles is wrapper { status, message, data: [] }
  const rawList = useMemo(() => {
    //return error?.data?.data
    if (Array.isArray(apiFiles)) return apiFiles;
    if (Array.isArray(apiFiles?.data)) return apiFiles.data;
    return [];
  }, [apiFiles]);

  const files = useMemo(() => {
    const mapped = rawList.map((f) => mapApiFileToUi(f, entityType));

    if (!search?.trim()) return mapped;
    const q = search.toLowerCase();
    return mapped.filter((x) => (x.name || "").toLowerCase().includes(q));
  }, [rawList, entityType, search]);

  // Auto-select first doc on load
  useEffect(() => {
    if (!selectedDocId && files.length > 0) setSelectedDocId(files[0].id);
    if (files.length === 0) {
      setSelectedDocId(null);
      setDetailsOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files.length]);

  const selectedIndex = useMemo(
    () => files.findIndex((f) => f.id === selectedDocId),
    [files, selectedDocId]
  );

  const selectedDoc = useMemo(
    () => files.find((f) => f.id === selectedDocId) || null,
    [files, selectedDocId]
  );

  console.log("selectedDoc", selectedDoc)

  const selectedVersions = useMemo(
    () => selectedDoc?._versions || [],
    [selectedDoc]
  );

  const openDetails = useCallback((docId) => {
    setSelectedDocId(docId);
    setDetailsOpen(true);
  }, []);

  const closeDetails = useCallback(() => setDetailsOpen(false), []);

  const canPrev = selectedIndex > 0;
  const canNext = selectedIndex >= 0 && selectedIndex < files.length - 1;

  const handlePrev = useCallback(() => {
    if (!canPrev) return;
    const prevDoc = files[selectedIndex - 1];
    if (prevDoc) setSelectedDocId(prevDoc.id);
  }, [canPrev, files, selectedIndex]);

  const handleNext = useCallback(() => {
    if (!canNext) return;
    const nextDoc = files[selectedIndex + 1];
    if (nextDoc) setSelectedDocId(nextDoc.id);
  }, [canNext, files, selectedIndex]);

  // Keyboard nav when drawer is open: ← / → / Esc
  useEffect(() => {
    if (!detailsOpen) return;

    const onKeyDown = (e) => {
      if (e.key === "ArrowLeft") handlePrev();
      if (e.key === "ArrowRight") handleNext();
      if (e.key === "Escape") closeDetails();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [detailsOpen, handlePrev, handleNext, closeDetails]);

  const handleDownload = async (fileId, version, fileName, key) => {
    // TODO: call API -> get SAS -> window.location = sasUrl
    console.log("download", fileId);
    try {

      setLoadingDownloadKey(key)
      const blob = await triggerDownload({ fileId, version }).unwrap();

      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName || 'download');
      document.body.appendChild(link);

      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download failed:", error);
    } finally {
      setLoadingDownloadKey(null)
    }
  };

  const handleUpload = async (values, cb) => {
    // TODO: open upload modal (new doc OR new version)
    console.log("upload clicked");
    // const reqBody = {
    //   projectId: projectId,

    // }
    try {


      const formData = new FormData();
      formData.append('ProjectId', projectId);
      formData.append('FolderName', entityType);
      formData.append('ObjectId', customId);
      formData.append('ObjectName', objectName);
      formData.append('File', values.file);

      await uploadFile(formData).unwrap();
    } catch (error) {
      console.error("Upload failed:", error);
    }
  };

  const handleRollback = (version) => {
    // TODO: POST /api/dms/documents/{id}/rollback/{version}
    console.log("rollback", selectedDocId, version);
  };

  const columns = useMemo(
    () => [
      {
        field: "name",
        name: "Name",
        flex: 1.6,
        minWidth: 220,
        renderer: (row) => (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
            <DescriptionOutlinedIcon fontSize="small" />
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: "13px", fontWeight: 600 }} noWrap>
                {row.name}
              </Typography>
              <Typography sx={subTextStyle} noWrap>
                {fileTypeFromName(row.name)}
              </Typography>
            </Box>
          </Stack>
        ),
      },
      {
        field: "version",
        name: "Version",
        width: 90,
        align: "center",
        headerAlign: "center",
        renderer: (row) => <Chip size="small" label={`v${row.version}`} />,
      },
      {
        field: "sizeBytes",
        name: "Size",
        width: 110,
        renderer: (row) => formatBytes(row.sizeBytes),
      },
      { field: "uploadedBy", name: "Uploaded By", width: 140 },
      {
        field: "uploadedOn",
        name: "Date",
        width: 120,
        renderer: (row) =>
          row?.uploadedOn ? moment(row.uploadedOn).format("DD MMM YYYY") : "N/A",
      },
    ],
    []
  );

  return (
    <>
      <Grid container spacing={2}>
        <Grid size={{ md: 12, xs: 12 }}>
          <Grid container spacing={2}>
            <Grid size={{ md: 12, xs: 12 }}>
              <Stack
                direction={{ xs: "column", md: "row" }}
                width="100%"
                alignItems={{ md: "center" }}
                spacing={1}
                justifyContent="space-between"
              >
                <Box sx={{ width: "100%" }}>
                  <Typography variant="h5" sx={{ mb: 1 }}>
                    Documents
                  </Typography>
                  <Typography>Upload documents, and manage versions.</Typography>
                </Box>

                {isView === false && <CustomButton
                  label="Upload"
                  startIcon={<CloudUploadOutlinedIcon />}
                  color="primary"
                  onClick={() => setIsUpload(true)}
                  sx={{
                    backgroundColor: "#00344C",
                    fontFamily: "Outfit",
                    fontWeight: 500,
                    fontSize: "14px",
                    lineHeight: "100%",
                    letterSpacing: "0px",
                  }}
                />}
              </Stack>

              {/* This wrapper is the Drawer container so it opens inside the dialog */}
              <Box
                ref={dialogBodyRef}
                sx={{
                  position: "relative",
                  height: "calc(100vh - 170px)",
                  mt: 2,
                  overflow: "hidden",
                }}
              >
                <Stack direction="row" spacing={2} sx={{ height: "100%", minWidth: 0 }}>
                  {/* Table */}
                  <Paper
                    sx={{
                      width: "100%",
                      p: 1.5,
                      borderRadius: 2,
                      overflow: "hidden",
                      mt: 1,
                      minWidth: 0,
                    }}
                  >
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Box>
                        <Typography sx={labelStyle}>Documents</Typography>
                      </Box>
                      <Typography sx={subTextStyle}>
                        {files.length} item{files.length === 1 ? "" : "s"}
                      </Typography>
                    </Stack>

                    <Divider sx={{ my: 1 }} />

                    {(isLoading || isFetching) && (
                      <Typography sx={subTextStyle}>Loading documents...</Typography>
                    )}

                    {isError && (
                      <Typography sx={{ ...subTextStyle, color: "error.main" }}>
                        Failed to load documents.{" "}
                        <span
                          style={{ textDecoration: "underline", cursor: "pointer" }}
                          onClick={refetch}
                        >
                          Retry
                        </span>
                      </Typography>
                    )}

                    {!isError && <Box sx={{ height: "calc(100% - 56px)" }}>
                      <TableComponent
                        data={files}
                        columns={columns}
                        onRowClick={(row) => openDetails(row.id)}
                        selectedRowId={selectedDocId}
                        createSortHandler={() => { }}
                      />
                    </Box>}
                  </Paper>

                  {/* Drawer rendered INSIDE dialog container */}
                  <Drawer
                    variant="temporary"
                    anchor="right"
                    open={detailsOpen}
                    onClose={closeDetails}
                    disablePortal
                    container={dialogBodyRef.current}
                    ModalProps={{
                      container: dialogBodyRef.current,
                      disablePortal: true,
                      keepMounted: true,
                    }}
                    PaperProps={{
                      sx: {
                        position: "absolute",
                        right: 0,
                        top: 0,
                        height: "100%",
                        width: { xs: "100%", sm: 420, md: 480 },
                        p: 2,
                        borderTopLeftRadius: 12,
                        borderBottomLeftRadius: 12,
                      },
                    }}
                  >
                    {/* Header + Next/Prev */}
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{ mb: 1 }}
                    >
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <Tooltip title="Previous (←)">
                          <span>
                            <IconButton size="small" onClick={handlePrev} disabled={!canPrev}>
                              <ChevronLeftOutlinedIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>

                        <Tooltip title="Next (→)">
                          <span>
                            <IconButton size="small" onClick={handleNext} disabled={!canNext}>
                              <ChevronRightOutlinedIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>

                        <Typography sx={{ ...labelStyle, ml: 1 }}>Details</Typography>
                      </Stack>

                      <IconButton size="small" onClick={closeDetails}>
                        <CloseOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Stack>

                    <Divider sx={{ mb: 1.5 }} />

                    {!selectedDoc ? (
                      <Box sx={{ mt: 2 }}>
                        <Typography sx={{ fontSize: "13px", fontWeight: 600 }}>
                          Select a document
                        </Typography>
                        <Typography sx={subTextStyle}>
                          Click a row to view details, version history, and actions.
                        </Typography>
                      </Box>
                    ) : (
                      <Box sx={{ overflow: "auto" }}>
                        <Typography sx={{ fontSize: "15px", fontWeight: 700 }} noWrap>
                          {selectedDoc.name}
                        </Typography>

                        <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap" }}>
                          <Chip size="small" label={`v${selectedDoc.version}`} />
                          <Chip
                            size="small"
                            variant="outlined"
                            label={fileTypeFromName(selectedDoc.name)}
                          />
                          <Chip
                            size="small"
                            variant="outlined"
                            label={formatBytes(selectedDoc.sizeBytes)}
                          />
                        </Stack>

                        <Divider sx={{ my: 1.5 }} />

                        <Stack spacing={0.6}>
                          <Typography sx={subTextStyle}>
                            <b>Uploaded by:</b> {selectedDoc.uploadedBy}
                          </Typography>
                          <Typography sx={subTextStyle}>
                            <b>Date:</b>{" "}
                            {selectedDoc?.uploadedOn
                              ? moment(selectedDoc.uploadedOn).format("DD MMM YYYY")
                              : "N/A"}
                          </Typography>
                          <Typography sx={subTextStyle}>
                            <b>Linked to:</b>{" "}
                            {(selectedDoc.linkedTo || [])
                              .map((x) => `${x.type} ${x.id}`)
                              .join(", ") || "-"}
                          </Typography>
                        </Stack>

                        <Divider sx={{ my: 1.5 }} />

                        <Stack direction="row" spacing={1}>
                          <CustomButton
                            label="Download"
                            startIcon={loadingDownloadKey === "main" ? <CircularProgress size="16px" /> : <DownloadOutlinedIcon />}
                            color="primary"
                            onClick={() => handleDownload(selectedDoc.id, selectedDoc?.version, selectedDoc?.name, "main")}
                            sx={{
                              backgroundColor: "#00344C",
                              fontFamily: "Outfit",
                              fontWeight: 500,
                              fontSize: "14px",
                              lineHeight: "100%",
                              letterSpacing: "0px",
                              flex: 0.9,
                            }}
                            disabled={loadingDownloadKey === "main"}
                          />
                          {isView === false && <CustomButton
                            label="Upload New Version"
                            startIcon={<CloudUploadOutlinedIcon />}
                            color="primary"
                            onClick={() => setIsUpload(true)}
                            sx={{
                              backgroundColor: "#00344C",
                              fontFamily: "Outfit",
                              fontWeight: 500,
                              fontSize: "14px",
                              lineHeight: "100%",
                              letterSpacing: "0px",
                              flex: 1.2,
                            }}
                          />}
                        </Stack>

                        <Divider sx={{ my: 1.5 }} />

                        <Typography sx={labelStyle}>Versions</Typography>
                        <List dense sx={{ mt: 0.5 }}>
                          {selectedVersions.map((v) => (
                            <ListItem
                              key={`${selectedDoc.id}-${v.version}`}
                              secondaryAction={
                                <Stack direction="row" spacing={0.5}>
                                  <Tooltip title="Download this version">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleDownload(selectedDoc?.id, v.version, selectedDoc?.name, v.version)}
                                    >
                                      {loadingDownloadKey === v.version ? <CircularProgress size={"15px"} /> : <DownloadOutlinedIcon fontSize="small" />}
                                    </IconButton>
                                  </Tooltip>

                                  {v.version !== selectedDoc.version && (
                                    <Tooltip title="Rollback (set as current)">
                                      <IconButton size="small" onClick={() => handleRollback(v.version)}>
                                        <RestoreOutlinedIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                </Stack>
                              }
                            >
                              <ListItemText
                                primary={
                                  <Typography sx={{ fontSize: "13px", fontWeight: 600 }}>
                                    v{v.version} •{" "}
                                    {v.date ? moment(v.date).format("DD MMM YYYY") : "N/A"}
                                  </Typography>
                                }
                                secondary={
                                  <Typography sx={subTextStyle}>
                                    {v.by}
                                    {v.note ? ` • ${v.note}` : ""}
                                  </Typography>
                                }
                              />
                            </ListItem>
                          ))}

                          {selectedVersions.length === 0 && (
                            <Typography sx={subTextStyle}>No version history found.</Typography>
                          )}
                        </List>

                        <Divider sx={{ my: 1.5 }} />

                        <Box sx={{ height: 220, border: "1px dashed #E5E7EB", borderRadius: 2, p: 1.5 }}>
                          <Typography sx={{ fontSize: "13px", fontWeight: 600 }}>
                            Preview (MVP)
                          </Typography>
                          <Typography sx={subTextStyle}>
                            In MVP, show inline preview for PDFs/images later. For now, download to view.
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  </Drawer>
                </Stack>
              </Box>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      {isUpload && <UploadDocument open={isUpload} handleClose={() => setIsUpload(false)} handleSubmit={handleUpload} />}
    </>
  );
};

export default DocumentManagement;
